import { FC, useMemo, useState, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import TitleBar from '../TitleBar';
import BottomNavigation from '../BottomNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ApplicantFormStepProps, PersonalInfoFormType } from '../../../../utils/types';
import { savePersonalInfo } from 'store/ohaFormReducer';
import { mapOHAForm } from 'screens/Mutations/utils/mappers/oha/mapFormToApi';
import { useOhaFormStep } from 'screens/Mutations/hooks/useOhaFormStep';
import { formatISO } from 'date-fns';
import { personalInfoSchema } from 'screens/Mutations/schemas/personalInfo';
import PersonalInfo from 'screens/Mutations/templates/PersonalInfo';
import FormStepper from '../FormStepper';
import FormTemplate from 'components/Templates/FormTemplate';
import { Form } from 'components/Molecules/components/Form/Form.styled';
import CustomSnackbar from 'components/Snackbar/CustomSnackbar';

const PersonalInfoForm: FC<ApplicantFormStepProps> = ({ title, subtitle, rootFormName }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState<{ status: string; name: string }>();

  const ohaFormState = useSelector((state: RootState) => state.ohaForm);
  const hasNewApplicantState = ohaFormState.newApplicant.personalInfo.hasNewApplicant;
  const defaultValues = useMemo(
    () => ({
      ...ohaFormState[rootFormName].personalInfo,
      hasNewApplicant: hasNewApplicantState || 'no',
    }),
    [hasNewApplicantState, ohaFormState, rootFormName]
  );

  const formMethods = useForm<PersonalInfoFormType>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(personalInfoSchema(rootFormName)),
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = formMethods;

  const personalInfoFormValues = watch();
  const fullName = personalInfoFormValues?.surname
    ? `${personalInfoFormValues?.firstNames || ''} ${personalInfoFormValues?.insertion || ''} ${
        personalInfoFormValues.surname
      }`
    : '';

  useEffect(() => {
    // defaultValue works well when your data is ready on the initial render,
    // however sometimes defaultValue may come from API or
    // delay, and that's when you should use reset
    reset(defaultValues);
  }, [defaultValues, reset, rootFormName]);

  const completeForm = {
    ...ohaFormState,
    [rootFormName]: {
      contactDetails: { ...ohaFormState[rootFormName].contactDetails },
      income: { ...ohaFormState[rootFormName].income },
      obligation: { ...ohaFormState[rootFormName].obligation },
      personalInfo: { ...personalInfoFormValues },
    },
  };

  const onSaveError = () => handleNotification('error', 'submit');

  const { save, isSaving, goToNextStep } = useOhaFormStep({ onSaveError });

  const onSubmit = async (formData: PersonalInfoFormType) => {
    const date = formData.dateOfBirth
      ? formatISO(formData.dateOfBirth as Date, { representation: 'date' })
      : undefined;

    const personalInfo = { ...formData, dateOfBirth: date };

    dispatch(
      savePersonalInfo({
        formData: personalInfo,
        rootFormName,
      })
    );

    if (isDirty) {
      await save(
        mapOHAForm({
          ...ohaFormState,
          [rootFormName]: { ...ohaFormState[rootFormName], personalInfo },
        })
      );
    }
    goToNextStep();
  };

  const dispatchForm = useCallback(() => {
    const date = personalInfoFormValues.dateOfBirth
      ? formatISO(new Date(personalInfoFormValues.dateOfBirth), { representation: 'date' })
      : undefined;

    const personalInfo = { ...personalInfoFormValues, dateOfBirth: date };

    dispatch(
      savePersonalInfo({
        formData: personalInfo,
        rootFormName,
      })
    );
  }, [rootFormName, personalInfoFormValues, dispatch]);

  const handleNotification = useCallback(
    (status: string, name: string) => setSnackbar({ status, name }),
    []
  );

  const handleOnClose = useCallback(() => setSnackbar(undefined), []);

  return (
    <>
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormTemplate
            header={
              <TitleBar
                onResult={handleNotification}
                formState={completeForm}
                resetForm={reset}
                isFormDirty={isDirty}
                dispatchCurrentForm={dispatchForm}
              />
            }
            sidebar={<FormStepper />}
            footer={
              <BottomNavigation dispatchCurrentForm={dispatchForm} isLoadingSubmit={isSaving} />
            }
          >
            <PersonalInfo
              title={title}
              subtitle={subtitle}
              name={fullName}
              rootFormName={rootFormName}
            />
          </FormTemplate>
        </Form>
      </FormProvider>
      {snackbar ? (
        <CustomSnackbar
          isOpen={!!snackbar}
          handleClose={handleOnClose}
          severity={snackbar.status === 'success' ? 'success' : 'error'}
          message={t(`directMutations.snackbar.${snackbar.name}.${snackbar.status}`)}
        />
      ) : null}
    </>
  );
};

export default PersonalInfoForm;
